import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash, Target, Flag } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function BI() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [defs, setDefs] = useState([]);
  const [mevents, setMevents] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [goals, setGoals] = useState([]);
  const [metric, setMetric] = useState({ key: "revenue.total", value: "" });
  const [kpi, setKpi] = useState({ name: "", metric_key: "revenue.total", target: "", comparator: ">=" });
  const [goal, setGoal] = useState({ name: "", description: "" });

  const load = () => {
    api.get("/bi/metrics").then((r) => setDefs(r.data)).catch(() => {});
    api.get("/bi/metrics/events").then((r) => setMevents(r.data)).catch(() => {});
    api.get("/bi/kpis").then((r) => setKpis(r.data)).catch(() => {});
    api.get("/bi/goals").then((r) => setGoals(r.data)).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const recordMetric = async () => {
    try {
      await api.post("/bi/metrics/events", { key: metric.key, value: Number(metric.value), dimensions: {} });
      setMetric({ ...metric, value: "" });
      load();
      toast.success("Metric recorded");
    } catch (e) { toast.error(apiError(e)); }
  };
  const addKpi = async () => {
    try { await api.post("/bi/kpis", { ...kpi, target: Number(kpi.target) }); setKpi({ name: "", metric_key: "revenue.total", target: "", comparator: ">=" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addGoal = async () => {
    try { await api.post("/bi/goals", goal); setGoal({ name: "", description: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delKpi = async (id) => { await api.delete(`/bi/kpis/${id}`); load(); };
  const delGoal = async (id) => { await api.delete(`/bi/goals/${id}`); load(); };

  return (
    <div>
      <PageHeader title={t("m2.biTitle")} subtitle={t("m2.biSub")} />
      <Tabs defaultValue="metrics">
        <TabsList className="rounded-sm">
          <TabsTrigger value="metrics" className="rounded-sm" data-testid="bi-tab-metrics">{t("m2.metrics")}</TabsTrigger>
          <TabsTrigger value="kpis" className="rounded-sm" data-testid="bi-tab-kpis">{t("m2.kpis")}</TabsTrigger>
          <TabsTrigger value="goals" className="rounded-sm" data-testid="bi-tab-goals">{t("m2.goals")}</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="space-y-4 mt-4">
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="grid md:grid-cols-[2fr_1fr_auto] gap-3 items-end">
              <div className="space-y-2">
                <Label>{t("m2.metricKey")}</Label>
                <Select value={metric.key} onValueChange={(v) => setMetric({ ...metric, key: v })}>
                  <SelectTrigger className="rounded-sm font-mono" data-testid="metric-key-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{defs.map((d) => <SelectItem key={d.key} value={d.key} className="font-mono">{d.key}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("m2.value")}</Label><Input type="number" value={metric.value} onChange={(e) => setMetric({ ...metric, value: e.target.value })} className="rounded-sm font-mono" data-testid="metric-value-input" /></div>
              <Button onClick={recordMetric} disabled={metric.value === ""} className="rounded-sm" data-testid="record-metric-button"><Plus size={16} className="mr-1" />{t("m2.recordMetric")}</Button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-sm overflow-x-auto">
              <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-bold">Metric definitions</h3></div>
              <Table>
                <TableHeader><TableRow><TableHead>Key</TableHead><TableHead>{t("common.name")}</TableHead><TableHead>Unit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {defs.map((d) => (
                    <TableRow key={d.key} data-testid={`metric-def-${d.key}`}>
                      <TableCell className="font-mono text-xs">{d.key}</TableCell>
                      <TableCell>{d.name}</TableCell>
                      <TableCell><Badge variant="outline" className="rounded-sm">{d.unit}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="bg-card border border-border rounded-sm overflow-x-auto">
              <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-bold">{t("m2.recentEvents")}</h3></div>
              <Table>
                <TableHeader><TableRow><TableHead>Key</TableHead><TableHead>{t("m2.value")}</TableHead><TableHead>{t("m2.time")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {mevents.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-mono text-xs">{e.key}</TableCell>
                      <TableCell className="font-mono text-xs">{e.value}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{(e.created_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="kpis" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 mb-4">
            <div className="grid md:grid-cols-[2fr_1.5fr_1fr_auto] gap-3 items-end">
              <div className="space-y-2"><Label>{t("common.name")}</Label><Input value={kpi.name} onChange={(e) => setKpi({ ...kpi, name: e.target.value })} className="rounded-sm" data-testid="kpi-name-input" /></div>
              <div className="space-y-2"><Label>Metric</Label>
                <Select value={kpi.metric_key} onValueChange={(v) => setKpi({ ...kpi, metric_key: v })}>
                  <SelectTrigger className="rounded-sm font-mono" data-testid="kpi-metric-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{defs.map((d) => <SelectItem key={d.key} value={d.key} className="font-mono">{d.key}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("m2.target")}</Label><Input type="number" value={kpi.target} onChange={(e) => setKpi({ ...kpi, target: e.target.value })} className="rounded-sm font-mono" data-testid="kpi-target-input" /></div>
              <Button onClick={addKpi} disabled={!kpi.name || kpi.target === ""} className="rounded-sm" data-testid="add-kpi-button"><Plus size={16} className="mr-1" />{t("m2.addKpi")}</Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {kpis.map((k) => (
              <div key={k.id} className="bg-card border border-border rounded-sm p-5 group" data-testid={`kpi-card-${k.id}`}>
                <div className="flex items-start justify-between">
                  <Target size={20} className="text-primary" weight="bold" />
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100" onClick={() => delKpi(k.id)}><Trash size={14} /></Button>
                </div>
                <h4 className="font-heading font-bold mt-3">{k.name}</h4>
                <div className="text-xs text-muted-foreground font-mono mt-1">{k.metric_key} {k.comparator} {k.target}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="goals" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 mb-4">
            <div className="grid md:grid-cols-[1.5fr_2fr_auto] gap-3 items-end">
              <div className="space-y-2"><Label>{t("common.name")}</Label><Input value={goal.name} onChange={(e) => setGoal({ ...goal, name: e.target.value })} className="rounded-sm" data-testid="goal-name-input" /></div>
              <div className="space-y-2"><Label>{t("knowledge.description")}</Label><Input value={goal.description} onChange={(e) => setGoal({ ...goal, description: e.target.value })} className="rounded-sm" data-testid="goal-desc-input" /></div>
              <Button onClick={addGoal} disabled={!goal.name} className="rounded-sm" data-testid="add-goal-button"><Plus size={16} className="mr-1" />{t("m2.addGoal")}</Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {goals.map((g) => (
              <div key={g.id} className="bg-card border border-border rounded-sm p-5 group" data-testid={`goal-card-${g.id}`}>
                <div className="flex items-start justify-between">
                  <Flag size={20} className="text-primary" weight="bold" />
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100" onClick={() => delGoal(g.id)}><Trash size={14} /></Button>
                </div>
                <h4 className="font-heading font-bold mt-3">{g.name}</h4>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{g.description || "—"}</p>
                <Badge className="rounded-sm mt-3 text-[10px] uppercase">{g.status}</Badge>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
